import { useMemo, useRef, useState } from "react";
import { Mention, MentionsInput } from "react-mentions";

import { getTaskProjectSuggestions, getTaskUserSuggestions } from "@/lib/api";

const defaultMentionsStyle = {
  control: {
    minHeight: 40,
    fontSize: 13,
    lineHeight: 1.5,
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    backgroundColor: "#ffffff",
    fontFamily: "var(--font)",
    overflow: "visible",
  },
  highlighter: {
    padding: "9px 12px",
    borderRadius: 10,
    overflow: "visible",
    color: "#0f172a",
    fontFamily: "var(--font)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  input: {
    margin: 0,
    padding: "9px 12px",
    outline: 0,
    border: 0,
    color: "transparent",
    WebkitTextFillColor: "transparent",
    caretColor: "#0f172a",
    backgroundColor: "transparent",
    fontFamily: "var(--font)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  suggestions: {
    list: {
      backgroundColor: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      boxShadow: "0 10px 28px rgba(15,23,42,0.12)",
      maxHeight: 240,
      overflowY: "auto",
      zIndex: 300,
    },
    item: {
      padding: "8px 10px",
      borderBottom: "1px solid #f1f5f9",
      cursor: "pointer",
      backgroundColor: "#ffffff",
      "&focused": {
        backgroundColor: "#eff6ff",
      },
    },
  },
};

const compactMentionsStyle = {
  control: {
    minHeight: 38,
    fontSize: 13,
    lineHeight: 1.5,
    border: "none",
    borderRadius: 10,
    backgroundColor: "transparent",
    fontFamily: "var(--font)",
    overflow: "visible",
  },
  highlighter: {
    padding: "8px 12px",
    borderRadius: 10,
    overflow: "visible",
    color: "#0f172a",
    fontFamily: "var(--font)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  input: {
    margin: 0,
    padding: "8px 12px",
    outline: 0,
    border: 0,
    color: "transparent",
    WebkitTextFillColor: "transparent",
    caretColor: "#0f172a",
    backgroundColor: "transparent",
    fontFamily: "var(--font)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  suggestions: {
    list: {
      backgroundColor: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      boxShadow: "0 10px 28px rgba(15,23,42,0.12)",
      maxHeight: 240,
      overflowY: "auto",
      zIndex: 300,
    },
    item: {
      padding: "8px 10px",
      borderBottom: "1px solid #f1f5f9",
      cursor: "pointer",
      backgroundColor: "#ffffff",
      "&focused": {
        backgroundColor: "#eff6ff",
      },
    },
  },
};

const mentionChipStyle = {
  backgroundColor: "#eef2ff",
  borderRadius: "999px",
  color: "#1e1b4b",
  display: "inline-block",
  lineHeight: "18px",
  padding: "1px 8px",
  margin: "0 1px",
  border: "1px solid #c7d2fe",
  fontWeight: 600,
};

export const TaggingInput = ({
  value,
  onChange,
  workspaceDomain,
  placeholder,
  testId,
  disabled = false,
  singleLine = true,
  compact = false,
}) => {
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const userDebounceRef = useRef(null);
  const projectDebounceRef = useRef(null);

  const userSuggestionLoader = useMemo(
    () =>
      (search, callback) => {
        if (userDebounceRef.current) {
          clearTimeout(userDebounceRef.current);
        }
        userDebounceRef.current = setTimeout(async () => {
          setIsLoadingUsers(true);
          try {
            const rows = await getTaskUserSuggestions({
              q: search || "",
              workspace_domain: workspaceDomain,
            });
            callback(
              rows.map((item) => ({
                id: item.email,
                display: item.name,
                role: item.role,
                email: item.email,
              })),
            );
          } catch (_error) {
            callback([]);
          } finally {
            setIsLoadingUsers(false);
          }
        }, 150);
      },
    [workspaceDomain],
  );

  const projectSuggestionLoader = useMemo(
    () =>
      (search, callback) => {
        if (projectDebounceRef.current) {
          clearTimeout(projectDebounceRef.current);
        }
        projectDebounceRef.current = setTimeout(async () => {
          setIsLoadingProjects(true);
          try {
            const rows = await getTaskProjectSuggestions({
              q: search || "",
              workspace_domain: workspaceDomain,
            });
            callback(
              rows.map((item) => ({
                id: item.id,
                display: item.name,
                client_name: item.client_name,
                health_band: item.health_band,
              })),
            );
          } catch (_error) {
            callback([]);
          } finally {
            setIsLoadingProjects(false);
          }
        }, 150);
      },
    [workspaceDomain],
  );

  const mentionsStyle = compact ? compactMentionsStyle : defaultMentionsStyle;

  return (
    <div className={compact ? "" : "space-y-1"} data-testid={`${testId}-container`}>
      <MentionsInput
        className={`tagging-mentions ${compact ? "tagging-mentions-compact" : ""}`.trim()}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={mentionsStyle}
        placeholder={placeholder}
        allowSuggestionsAboveCursor
        singleLine={singleLine}
        disabled={disabled}
        customSuggestionsContainer={(children) => (
          <div data-testid="tagging-suggestions-dropdown" className="tagging-suggestions-dropdown">
            <div data-testid={`${testId}-suggestions-dropdown`}>{children}</div>
          </div>
        )}
        data-testid={testId}
      >
        <Mention
          trigger="@"
          data={userSuggestionLoader}
          markup="@[__display__](__id__)"
          displayTransform={(id, display) => `@${display}`}
          appendSpaceOnAdd
          style={mentionChipStyle}
          renderSuggestion={(suggestion, _search, highlightedDisplay) => (
            <div className="flex items-center justify-between gap-2" data-testid={`${testId}-suggestion-user-${String(suggestion.id).replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
              <span className="truncate text-sm font-medium text-slate-800">{highlightedDisplay}</span>
              <span className="truncate text-[11px] uppercase text-slate-500">{suggestion.role?.replace?.("_", " ")}</span>
            </div>
          )}
        />
        <Mention
          trigger="#"
          data={projectSuggestionLoader}
          markup="#[__display__](__id__)"
          displayTransform={(id, display) => `#${display}`}
          appendSpaceOnAdd
          style={{ ...mentionChipStyle, backgroundColor: "#dbeafe" }}
          renderSuggestion={(suggestion, _search, highlightedDisplay) => (
            <div className="flex items-center justify-between gap-2" data-testid={`${testId}-suggestion-project-${String(suggestion.id).replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
              <span className="truncate text-sm font-medium text-slate-800">{highlightedDisplay}</span>
              <span className="truncate text-[11px] text-slate-500">{suggestion.client_name}</span>
            </div>
          )}
        />
      </MentionsInput>
      {(isLoadingUsers || isLoadingProjects) ? (
        <p className="text-[11px] text-slate-500" data-testid={`${testId}-loading-state`}>
          Loading suggestions...
        </p>
      ) : null}
    </div>
  );
};
